import { Link } from 'react-router-dom';

import { useMyTechEducation } from '@/pages/home/useMyTechEducation';
import { daysUntil } from '@/pages/home/widgets/format';
import { WidgetFrame } from '@/pages/home/widgets/WidgetFrame';

/**
 * 기술인 교육 — 내 승급교육·계속교육·교육비 환급을 한 칸에 (DOC-61 §6).
 *
 * <h2>인사 정보 위젯에 섞지 않은 이유</h2>
 * <b>대상이 다르다.</b> 인사 정보는 전 직원이 보는 개인 자료이고 교육 현황은 기술인만 해당한다.
 * 섞으면 해당 없는 사람의 홈에 늘 빈 칸이 남고, 빈 칸이 늘 보이는 위젯은 곧 아무도 안 읽는다.
 * 겹치는 값도 없다 — 저쪽의 「자격증 만료」 는 자격 자체의 유효기간이고, 이쪽의 「계속교육 만료」 는
 * 지나면 <b>승급 진단이 미이수로 세는</b> 이수 실적이다(다른 표 · 다른 날짜).
 *
 * <h2>기술인이 아니면 <b>아무것도 그리지 않는다</b></h2>
 * 등급·법정교육·협회 명부 기록이 하나도 없는 사람에게는 서버가 자격을 주지 않아 카탈로그에서
 * 빠진다(`GET /v1/home/widgets/conditions` · 사용자 지시 2026-08-27). 위젯 관리 목록에도 없다
 * ({@code onlyWhenEligible}).
 *
 * <p>그래도 카탈로그와 내용이 <b>한 박자 어긋나는</b> 순간이 있다 — 기록을 지운 직후, 또는
 * 자격 질의가 아직 안 돌아온 사이다. 한때 그 틈을 「등록된 기술등급이 없습니다」 안내문으로
 * 메웠는데, <b>0건인 사람에게는 그 한 줄도 나오면 안 된다</b>(사용자 지시 2026-09-16).
 * 그래서 {@code technician} 이 false 면 {@code null} 을 돌려준다 — 칸 자체가 서지 않는다.
 *
 * <h2>다섯 줄이 각각 다른 급함을 말한다</h2>
 * <ul>
 *   <li><b>법정교육 미이수</b> — <b>협회 명부에 그렇게 적혀 있다</b>(V287). 다툴 여지가 없는
 *       줄이라 맨 위다. 원장에 이수 기록이 있어도 협회가 미이수면 신고가 안 된 것이고,
 *       그 사람에게는 아직 할 일이 남았다(사용자 결정 2026-09-16).</li>
 *   <li><b>승급교육</b> — 협회가 「승급교육 이수 후 등급」 칸에 적어 준 줄만. 그 칸이 비면
 *       이 줄은 서지 않는다 — 승급 대상인지는 <b>협회가 판단해 적어 주는 것</b>이고,
 *       우리 기준표로 대신 세면 없는 말을 지어낸다(사용자 지적 2026-09-16).</li>
 *   <li><b>계속교육 만료</b> — 지나면 <b>이수하지 않은 것으로 센다</b>. 가진 것을 잃는 유일한 줄이다.</li>
 *   <li><b>수강 확정</b> — 결재는 끝났는데 아직 안 받았다. 지금 해야 하는 일이다.</li>
 *   <li><b>환급</b> — 회사가 나에게 줄 돈. 기한이 지났으면 물어봐야 한다.</li>
 * </ul>
 *
 * <h2>D-day 는 여기서 센다</h2>
 * 서버는 만료 여부({@code expired})만 판정한다 — 그 값은 승급 진단이 세는 것과 같아야 하기 때문이다.
 * 남은 날 수는 <b>보는 사람의 오늘</b>이라 화면이 센다(자격증 갱신 배지와 같은 규칙).
 */

/** 만료가 이 일수 안이면 챙겨야 한다. 계속교육은 3년짜리라 자격증(90일)보다 넉넉히 잡는다. */
const EXPIRY_SOON_DAYS = 120;

export function TechEducationWidget() {
  const status = useMyTechEducation();
  const data = status.data ?? null;

  const expiries = data?.expiries ?? [];
  const inProgress = data?.inProgress ?? [];
  const refunds = data?.refunds ?? [];
  const association = data?.associationEducations ?? [];
  const promotionEducations = data?.promotionEducations ?? [];

  /*
    기술인이 아니면 <b>칸 자체를 세우지 않는다</b>(사용자 지시 2026-09-16).

    받아 보고 나서 판단한다 — 아직 응답이 없을 때({@code data == null})는 아래로 내려가
    {@code pending} 으로 그린다. 여기서 같이 접으면 홈을 열 때마다 이 칸이 <b>깜빡였다 서는</b>
    모양이 된다.
  */
  if (data && !data.technician) {
    return null;
  }

  const empty =
    expiries.length === 0 && inProgress.length === 0 && refunds.length === 0
    && association.length === 0 && promotionEducations.length === 0;

  return (
    <WidgetFrame
      title="기술인 교육"
      icon="clipboard"
      to="/approval/draft?formKey=TECH_EDU_APPLY"
      toLabel="교육 신청"
      pending={status.isPending}
      error={status.error}
      empty={empty}
      emptyText="챙길 교육과 환급이 없습니다."
    >
      <div className="space-y-3">
        {/*
          협회 명부가 말한 미이수가 <b>맨 위</b>다. 이 줄은 협회에 그렇게 적혀 있다는 뜻이라
          아래 줄들(우리 원장·우리 기준표)보다 다툴 여지가 없다.
        */}
        {association.length > 0 && (
          <Section title="법정교육 미이수">
            {association.map((notice) => (
              <p key={notice.key} className="text-ui-sm" style={{ color: 'var(--danger)' }}>
                {notice.message}
              </p>
            ))}
          </Section>
        )}

        {/*
          승급교육은 <b>협회 명부가 근거의 전부</b>다 — 「승급교육 이수 후 등급」 칸에 등급이
          적혀 있는 줄만 선다.

          한때 역량지수 기준표 진단({@code promotions[].educationShortfall})도 이 자리를 함께
          채웠다. 그런데 <b>법정교육 원장은 결재를 거쳐야만 차는 표</b>라 대부분의 기술인에게
          비어 있어서, 협회가 승급교육을 한 마디도 하지 않은 사람에게 「승급교육을 이수해
          주세요」 가 떴다(사용자 지적 2026-09-16). 화면이 없는 말을 지어낸 것이다.
        */}
        {promotionEducations.length > 0 && (
          <Section title="승급교육">
            {promotionEducations.map((notice) => (
              <p key={notice.key} className="text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
                {notice.message}
              </p>
            ))}
          </Section>
        )}

        {expiries.length > 0 && (
          <Section title="계속교육 유효기간">
            {expiries.map((expiry) => (
              <div key={expiry.educationId} className="flex items-center gap-2">
                <span className="flex-1 truncate text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
                  {expiry.courseName || expiry.label}
                </span>
                <ExpiryBadge validUntil={expiry.validUntil} expired={expiry.expired} />
              </div>
            ))}
          </Section>
        )}

        {inProgress.length > 0 && (
          // 눌러서 갈 곳이 생겼다(V264) — 이수 후 수료증·영수증을 내는 화면이다. 종전 배지는
          // 「이수 후 정산」 이었는데, 그 말은 <b>본인이 결재를 올려야 한다</b>는 뜻이었다.
          // 이제 올리는 것은 파일 둘이고 결재는 담당 확인이 대신 세운다(DOC-61 §26).
          <Section title="수강 확정 · 미이수">
            {inProgress.map((pending) => (
              <Link
                key={pending.educationId}
                to={`/hr/tech-education/evidence?education=${pending.educationId}`}
                className="flex items-center gap-2 hover:underline"
              >
                <span className="flex-1 truncate text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
                  {pending.courseName || pending.label}
                  {pending.institution ? ` · ${pending.institution}` : ''}
                </span>
                <span className="badge badge-blue flex-shrink-0 badge-sm">
                  증빙 제출
                </span>
              </Link>
            ))}
          </Section>
        )}

        {refunds.length > 0 && (
          <Section title="교육비 환급">
            {refunds.map((refund) => (
              <div key={refund.educationId} className="flex items-center gap-2">
                <span className="flex-1 truncate text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
                  {refund.courseName || refund.label}
                  {refund.fee != null ? ` · ${refund.fee.toLocaleString()}원` : ''}
                </span>
                <RefundBadge status={refund.refundStatus} dueOn={refund.refundDueOn} />
              </div>
            ))}
          </Section>
        )}

        {/*
          마지막 대조일. 본인에게는 「내 기록이 협회와 맞춰진 적이 있는가」 이고, 담당자에게는
          밀린 것이 보이는 자리다 — 알림으로 재촉하는 대신 상태를 늘 띄워 둔다(DOC-61 §5).
        */}
        {data?.technician && (
          <p className="text-ui-xs" style={{ color: 'var(--text-muted)' }}>
            협회 대조 {data.lastVerifiedOn ? `${data.lastVerifiedOn} 확인` : '기록 없음'}
          </p>
        )}
      </div>
    </WidgetFrame>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-ui-xs" style={{ fontWeight: 600, color: 'var(--text-muted)' }}>
        {title}
      </p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

/**
 * 지났으면 '만료', 임박하면 'D-n', 그 외에는 만료연월.
 *
 * 만료 여부는 <b>서버 값을 쓴다</b>({@code expired}) — 승급 진단이 「이수하지 않은 것으로 센다」 고
 * 판단한 그 값이라, 화면이 따로 세면 위젯과 진단이 하루 차이로 갈릴 수 있다.
 */
function ExpiryBadge({ validUntil, expired }: { validUntil: string; expired: boolean }) {
  if (expired) {
    return <span className="badge badge-red badge-sm">만료</span>;
  }
  const left = daysUntil(validUntil);
  if (left <= EXPIRY_SOON_DAYS) {
    return <span className="badge badge-yellow badge-sm">D-{left}</span>;
  }
  return (
    <span className="badge badge-gray badge-sm">
      {validUntil.slice(0, 7).replace('-', '.')}
    </span>
  );
}

/** 지급 완료는 회색, 대기는 기한까지 D-n, 기한이 지났으면 빨강. */
function RefundBadge({ status, dueOn }: { status: 'NONE' | 'PENDING' | 'PAID'; dueOn: string | null }) {
  if (status === 'PAID') {
    return <span className="badge badge-gray badge-sm">지급 완료</span>;
  }
  if (!dueOn) {
    return <span className="badge badge-blue badge-sm">환급 대기</span>;
  }
  const left = daysUntil(dueOn);
  if (left < 0) {
    return <span className="badge badge-red badge-sm">기한 초과</span>;
  }
  return <span className="badge badge-blue badge-sm">D-{left}</span>;
}
